<template>
    <section class="kyc-modal">
        <div class="kyc-modal__title">{{ $t('kycUserIdentity.limitsAndAvailableFeatures') + '\n' + $t(`profile.kyc.level${modalLevel}Text`) }}</div>

        <div class="kyc-modal__content">
            <div class="kyc-modal__heading">{{ $t(`kycLevelUpModal.${currencyShortCode}.tableTitle`) }}</div>

            <div class="kyc-table">
                <div class="kyc-table__header">
                    <div>{{ $t('kycEmailVerification.action') }}</div>
                    <div class="kyc-table__bold-text">{{ $t(`profile.kyc.level${modalLevel - 1}VerifiedText`) }}</div>
                    <div>{{ $t(`profile.kyc.level${modalLevel}VerifiedText`) }}</div>
                </div>

                <div class="kyc-table__row">
                    <div>{{ $t(`kycLevelUpModal.${currencyShortCode}.tableTopup`) }}</div>
                    <div class="kyc-table__bold-text">
                        {{ formatNumber(currentLevel.topUpAndReceiveLimit) }}
                        <span v-if="!isUsaRegulation">{{ displayCurrency(currentLevel) }} {{ currentLevel.sendLimitFiatCurrency }}</span>
                    </div>
                    <div>
                        {{ formatNumber(nextLevel.topUpAndReceiveLimit) }}
                        <span v-if="!isUsaRegulation">{{ displayCurrency(nextLevel) }} {{ nextLevel.sendLimitFiatCurrency }} </span>
                    </div>
                </div>

                <div v-if="!isUsaRegulation"
                    class="kyc-table__row kyc-table__row--rounded"
                >
                    <div>{{ $t(`kycLevelUpModal.${currencyShortCode}.tableSend`) }}</div>
                    <div class="kyc-table__bold-text">
                        {{ formatNumber(currentLevel.sendLimit) }}
                        <span>{{ displaySendLimitCurrency(currentLevel) }} {{ currentLevel.topUpAndReceiveLimitFiatCurrency }}</span>
                    </div>
                    <div>
                        {{ formatNumber(nextLevel.sendLimit) }}
                        <span>{{ displaySendLimitCurrency(nextLevel) }} {{ nextLevel.topUpAndReceiveLimitFiatCurrency }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="kyc-modal__content">
            <div class="kyc-modal__heading">{{ $t('kycEmailVerification.availableFeatures') }}</div>

            <div class="kyc-table">
                <div class="kyc-table__header">
                    <div>{{ $t('kycEmailVerification.feature') }}</div>
                    <div class="kyc-table__bold-text">{{ $t(`profile.kyc.level${modalLevel - 1}VerifiedText`) }}</div>
                    <div>{{ $t(`profile.kyc.level${modalLevel}VerifiedText`) }}</div>
                </div>

                <div v-for="(item, index) in updatedFeatures"
                    :key="index"
                    class="kyc-table__row kyc-table__row--centered"
                >
                    <div>{{ item.feature }}</div>

                    <template v-if="!item.icon[modalLevel - 1] && !item.icon[modalLevel]">
                        <div class="kyc-table__unavailable-feature">
                            {{ `${$t(`kycUserIdentity.availableFromLevel${modalLevel + 1}`)}` }}
                        </div>
                    </template>

                    <template v-else>
                        <div>
                            <i :class="`icon-${item.icon[modalLevel - 1] ? 'check' : 'cancel-circle'}`" />
                        </div>
                        <div>
                            <i :class="`icon-${item.icon[modalLevel] ? 'check' : 'cancel-circle'}`" />
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import {
    mapGetters,
} from 'vuex';

// Utils
import {
    formatNumber,
} from '@shared/utils/formatter.js';

// Mixins
import kycMixin from '@webapp/helpers/kyc-mixin.js';

export default {
    name: 'kyc-modal',
    mixins: [kycMixin],
    data() {
        return {
            features: [
                {
                    feature: this.$t('kycEmailVerification.login'),
                    icon: [true, true, true, true, true],
                    isChzRestricted: false,
                },
                {
                    feature: this.$t('kycEmailVerification.tokenHunt'),
                    icon: [true, true, true, true, true],
                    isChzRestricted: false,
                },
                {
                    feature: this.$t('kycEmailVerification.voucherRedeem'),
                    icon: [false, true, true, true, true],
                    isChzRestricted: false,
                },
                {
                    feature: this.$t('kycEmailVerification.voteInSurveys'),
                    icon: [true, true, true, true, true],
                    isChzRestricted: false,
                },
                {
                    feature: this.$t('kycEmailVerification.topupChz'),
                    icon: [true, true, true, true, true],
                    isChzRestricted: true,
                },
                {
                    feature: this.$t('kycEmailVerification.sendChz'),
                    icon: [false, false, true, true, true],
                    isChzRestricted: true,
                },
                {
                    feature: this.$t('kycEmailVerification.receiveChz'),
                    icon: [false, true, true, true, true],
                    isChzRestricted: true,
                },
                {
                    feature: this.$t('kycEmailVerification.purchaseFanToken'),
                    icon: [true, true, true, true, true],
                    isChzRestricted: false,
                },
                {
                    feature: this.$t('kycEmailVerification.sellFanToken'),
                    icon: [false, false, true, true, true],
                    isChzRestricted: false,
                },
            ],
        };
    },
    computed: {
        ...mapGetters(['modal', 'isUsaRegulation']),

        ...mapGetters('wallet', ['isChzAllowed']),

        modalLevel() {
            return Object.keys(this.modal.data).length
                ? this.modal.data.modalLevel
                : 1;
        },

        updatedFeatures() {
            if (!this.isChzAllowed) {
                return this.features.filter((feature) => !feature.isChzRestricted);
            }

            return this.features;
        },

        currencyShortCode() {
            return this.defaultCurrency?.shortCode.toLowerCase();
        },
    },
    watch: {
        modalLevel(newValue) {
            this.kycMixinSetLevel(newValue - 1);
        },
    },
    created() {
        this.kycMixinSetLevel(this.modalLevel - 1);
    },
    methods: {
        formatNumber,

        getFormattedNumber(number) {
            return formatNumber(number);
        },
        displayCurrency(level) {
            const currency = level.topUpAndReceiveLimitFiatCurrency || '';

            return currency ? this.formatNumber(level.topUpAndReceiveLimitFiat) : '';
        },
        displaySendLimitCurrency(level) {
            let currency = level.sendLimitFiatCurrency || '';

            // If currency is not defined, it should be taken from previous level, in order to show "0" instead of empty space if possible
            // It can happen if value from backend is not sent at all/undefined
            if (currency) {
                this.currentSendLimitCurrency = currency;
            } else if (this.currentSendLimitCurrency) {
                currency = this.currentSendLimitCurrency;
            }

            return currency ? this.formatNumber(level.sendLimitFiat) : '';
        },
    },
};
</script>

<style lang="scss" scoped>
.kyc-modal {
    &__title {
        padding-left: 1.5rem;
        font-size: 2.5rem;
        font-weight: 700;
        line-height: 2.93rem;
        white-space: pre-line;
        color: $cyprus-color;

        @include max-width(sm) {
            font-size: 2rem;
        }
    }

    &__content {
        margin-top: 8rem;
    }

    &__heading {
        padding-left: 1.5rem;
        font-size: 2rem;
        line-height: 2.7rem;
        font-weight: 700;
        color: $cyprus-color;

        @include max-width(sm) {
            font-size: 1.6rem;
        }
    }
}

.kyc-table {
    padding-top: 1.6rem;

    &__header,
    &__row {
        padding: 1rem 1.5rem;
        display: flex;
        justify-content: flex-end;
        font-size: 1.5rem;
        color: $cyprus-color;

        @include max-width(sm) {
            font-size: 1.2rem;
        }

        div {
            min-width: 10rem;
            text-align: right;

            &:first-of-type {
                justify-self: flex-start;
                margin-right: auto;
                text-align: left;
            }

            &:last-of-type {
                margin-left: 3rem;
            }

            @include max-width(sm) {
                min-width: 5rem;
            }

            span {
                display: block;
                font-size: 1.2rem;
                color: rgba($cyprus-color, 0.5);
            }

            i {
                color: $dusty-gray-color;
                font-size: 2rem;
                position: relative;
                top: 0.2rem;

                &.icon-check {
                    color: $lima-color;
                }
            }
        }
    }

    &__header {
        padding: 3.8rem 1.5rem 1rem;
        background: rgba($cyprus-color, 0.1);
        border-radius: 0.575rem 0.575rem 0 0;
        border-bottom: 0.115rem solid $nobel-color;

        :nth-child(n + 1) {
            width: min-content;
            text-align: right;
        }
    }

    &__row {
        border-bottom: 0.115rem solid $light-gainsboro-color;

        &:nth-child(odd) {
            background: $alabaster-color;
        }

        &--centered {
            align-items: center;
        }

        &--rounded {
            border: none;
            border-radius: 0 0 0.575rem 0.575rem;
        }
    }

    &__unavailable-feature {
        font-size: 1.2rem;
        border-radius: 1.2rem;
        box-sizing: border-box;
        background: $white-color;
        padding: 0.2rem 1.5rem;
        border: 0.1rem solid $dusty-gray-color;
        white-space: nowrap;

        @include max-width(sm) {
            font-size: 1rem;
        }
    }

    &__bold-text {
        font-weight: 700;
    }
}
</style>